import type {
  CornerDotType,
  CornerSquareType,
  DotType,
  DrawType,
  Gradient,
  ShapeType,
} from "qr-code-styling"
import { baileLogoSvg, daVinciLogoSvg, kidsLogoSvg } from "./qrLogoAssets"

export interface QrData {
  qrBase: string
  nombre: string
  apellido: string
  fechaEvento: string
  tarjetasPagadas: number
  tarjetasPagadasMenores: number
  tipoEvento?: string
  categoria?: string
  dni?: number | null
}

export interface QrConfig {
  // ── Metadatos internos ────────────────────────────────────────────
  id: string
  suffix?: string
  titulo: string
  campoTarjetas: "tarjetasPagadas" | "tarjetasPagadasMenores" | "tarjetasBaile"
  visibleSi: (data: QrData) => boolean

  // ── Dimensiones ───────────────────────────────────────────────────
  size: number
  margin?: number

  // ── Forma y motor de renderizado ──────────────────────────────────
  shape?: ShapeType // "square" | "circle"
  drawType?: DrawType // "canvas" | "svg"

  // ── Logo central ─────────────────────────────────────────────────
  image?: string
  imageOptions?: {
    hideBackgroundDots?: boolean
    imageSize?: number // proporción 0.0–1.0 (ej: 0.3)
    crossOrigin?: string // "anonymous" para imágenes externas
    margin?: number
  }

  // ── Opciones del código QR ────────────────────────────────────────
  qrOptions?: {
    errorCorrectionLevel?: "L" | "M" | "Q" | "H"
  }

  // ── Puntos del QR ────────────────────────────────────────────────
  dotsOptions?: {
    type?: DotType // "square"|"dots"|"rounded"|"extra-rounded"|"classy"|"classy-rounded"
    color?: string
    gradient?: Gradient
  }

  // ── Cuadrado exterior de las 3 esquinas ──────────────────────────
  cornersSquareOptions?: {
    type?: CornerSquareType // "dot"|"square"|"extra-rounded" + DotType
    color?: string
    gradient?: Gradient
  }

  // ── Punto interior de las 3 esquinas ─────────────────────────────
  cornersDotOptions?: {
    type?: CornerDotType // "dot"|"square" + DotType
    color?: string
    gradient?: Gradient
  }

  // ── Fondo ─────────────────────────────────────────────────────────
  backgroundOptions?: {
    color?: string
    gradient?: Gradient
    round?: number // redondeo de esquinas del fondo 0.0–1.0
  }

  // ── Plantilla de tarjeta ─────────────────────────────────────────
  cardTemplate?: "default" | "fiesta"
  horario?: string // ej: "20pm a 06am" — se muestra debajo de la fecha en plantilla fiesta
}

export const QR_CONFIGS: QrConfig[] = [
  {
    id: "adultos-egr",
    size: 200,
    titulo: "Tarjetas Adultos",
    campoTarjetas: "tarjetasPagadas",
    image: daVinciLogoSvg("#9d2028", "#ffffff"),
    imageOptions: { crossOrigin: "anonymous", margin: 4, imageSize: 0.3 },
    drawType: "canvas",
    qrOptions: { errorCorrectionLevel: "M" },
    dotsOptions: { type: "rounded", color: "#aa2228" },
    backgroundOptions: { color: "#ffffff" },
    visibleSi: (d) =>
      d.tarjetasPagadas > 0 &&
      d.categoria !== "BAILE" &&
      (d.tipoEvento === "EGRESADOS" || d.tipoEvento == null),
  },
  {
    id: "menores-egr",
    size: 200,
    suffix: "-V",
    titulo: "Tarjetas Menores",
    campoTarjetas: "tarjetasPagadasMenores",
    image: kidsLogoSvg("#2244aa"),
    imageOptions: { crossOrigin: "anonymous", margin: 4, imageSize: 0.3 },
    drawType: "canvas",
    qrOptions: { errorCorrectionLevel: "M" },
    dotsOptions: { type: "rounded", color: "#2244aa" },
    backgroundOptions: { color: "#ffffff" },
    visibleSi: (d) =>
      d.tarjetasPagadasMenores > 0 &&
      d.categoria !== "BAILE" &&
      (d.tipoEvento === "EGRESADOS" || d.tipoEvento == null),
  },
  {
    id: "baile-egr",
    size: 200,
    suffix: "-B",
    titulo: "Tarjetas Baile",
    campoTarjetas: "tarjetasBaile",
    image: baileLogoSvg("#5516c2"),
    imageOptions: { crossOrigin: "anonymous", margin: 4, imageSize: 0.4 },
    drawType: "canvas",
    qrOptions: { errorCorrectionLevel: "M" },
    dotsOptions: { type: "rounded", color: "#5516c2ff" },
    backgroundOptions: { color: "#ffffff" },
    visibleSi: (d) => d.tarjetasPagadas > 0 && d.categoria === "BAILE",
  },
  {
    id: "adultos-cumple",
    size: 200,
    titulo: "Tarjetas Adultos",
    campoTarjetas: "tarjetasPagadas",
    image: daVinciLogoSvg("#ce6a05", "#ffffff"),
    imageOptions: { crossOrigin: "anonymous", margin: 4, imageSize: 0.3 },
    drawType: "canvas",
    qrOptions: { errorCorrectionLevel: "M" },
    dotsOptions: { type: "rounded", color: "#ce6a05ff" },
    backgroundOptions: { color: "#ffffff" },
    visibleSi: (d) =>
      d.tarjetasPagadas > 0 &&
      d.tipoEvento === "CUMPLEANIOS_BODAS" &&
      d.categoria !== "BAILE",
  },
  {
    id: "menores-cumple",
    size: 200,
    suffix: "-V",
    titulo: "Tarjetas Menores",
    campoTarjetas: "tarjetasPagadasMenores",
    image: kidsLogoSvg("#2244aa"),
    imageOptions: { crossOrigin: "anonymous", margin: 4, imageSize: 0.3 },
    drawType: "canvas",
    qrOptions: { errorCorrectionLevel: "M" },
    dotsOptions: { type: "rounded", color: "#2244aa" },
    backgroundOptions: { color: "#ffffff" },
    visibleSi: (d) =>
      d.tarjetasPagadasMenores > 0 &&
      d.tipoEvento === "CUMPLEANIOS_BODAS" &&
      d.categoria !== "BAILE",
  },
  {
    id: "adultos-fiesta",
    size: 200,
    titulo: "Tarjetas Adultos",
    campoTarjetas: "tarjetasPagadas",
    qrOptions: { errorCorrectionLevel: "M" },
    dotsOptions: { type: "rounded", color: "#8b0000" },
    cornersSquareOptions: { type: "square", color: "#8b0000" },
    cornersDotOptions: { type: "square", color: "#8b0000" },
    backgroundOptions: { color: "#ffffff", round: 0.1 },
    cardTemplate: "fiesta",
    horario: "20pm a 06am",
    visibleSi: (d) => d.tarjetasPagadas > 0 && d.tipoEvento === "FIESTAS",
  },
  {
    id: "menores-fiesta",
    size: 200,
    suffix: "-V",
    titulo: "Tarjetas Menores",
    campoTarjetas: "tarjetasPagadasMenores",
    qrOptions: { errorCorrectionLevel: "M" },
    dotsOptions: { type: "rounded", color: "#8b0000" },
    cornersSquareOptions: { type: "square", color: "#8b0000" },
    cornersDotOptions: { type: "square", color: "#8b0000" },
    backgroundOptions: { color: "#ffffff", round: 0.1 },
    cardTemplate: "fiesta",
    horario: "20pm a 06am",
    visibleSi: (d) =>
      d.tarjetasPagadasMenores > 0 && d.tipoEvento === "FIESTAS",
  },
]
